<script lang="ts">
    import type { SvelteComponent } from 'svelte';
    import type {
        ItemKind,
        Section,
    } from '@jet-app/music-classical/models/section/Section';
    import type { DefaultHeader } from '@jet-app/music-classical/models/header';
    import type { Lockup } from '~/jet/models';
    import Header from '@amp/web-app-components/src/components/Header/Header.svelte';
    import AccessoryButton from '~/components/jet/buttons/AccessoryButton/AccessoryButton.svelte';

    const ENABLE_CUSTOM_COLUMN_FOR_TYPES = [
        'brickLockup',
        'linkLockup',
        'rectangularLinkLockup',
    ];

    export let section: Section<Lockup>;
    export let itemComponent: typeof SvelteComponent;
    export let flowStyle: 'row' | 'column' = 'row';
    export let customColumnType: ItemKind | null = null;

    $: header = section?.header?.item as DefaultHeader;

    $: enableCustomColumns =
        ENABLE_CUSTOM_COLUMN_FOR_TYPES.includes(customColumnType);
</script>

{#if section.title}
    <div
        id={section.id}
        data-testid="grid-section-title-container"
        class="grid-section-title-container"
    >
        <Header title={section.title}>
            <svelte:fragment slot="buttons">
                {#if header}
                    {#each header?.accessoryButtons as button}
                        <AccessoryButton {button} />
                    {/each}
                {/if}
            </svelte:fragment>
        </Header>
    </div>
{/if}
<ul
    class="grid"
    class:grid--flow-row={flowStyle === 'row'}
    class:grid--flow-column={flowStyle === 'column'}
    class:grid--custom-columns={enableCustomColumns}
    class:grid--category-bricks={customColumnType === 'brickLockup'}
    class:grid--link-lockups={customColumnType === 'linkLockup'}
    class:grid--rectangular-link-lockups={customColumnType ===
        'rectangularLinkLockup'}
    data-testid="grid"
    id={section.id}
>
    {#each section?.items as item}
        <li class="grid-item" data-testid="grid-item">
            <svelte:component this={itemComponent} {item} />
        </li>
    {/each}
</ul>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/selectors' as *;
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use '@amp/web-shared-styles/app/core/mixins/room-grid' as *;
    @use '@amp/web-shared-styles/app/core/mixins/margin-gutter' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .grid {
        $grid-gap: 10px;
        $grid-gap-large: 20px;

        @include margin-gutter(0, $global-shelf-spacer-bottom);

        &.grid--flow-row {
            display: grid;
            grid-gap: $grid-gap;

            @media (--small) {
                grid-gap: $grid-gap-large;
            }

            &.grid--category-bricks {
                --numBrickColumns: 2;
                grid-template-columns: repeat(var(--numBrickColumns), 1fr);
                padding-top: 30px;

                :global(a) {
                    display: block; // Display focus ring
                    border-radius: $global-border-radius-large;
                }

                @media (--small) {
                    --numBrickColumns: 3;
                    padding-top: 18px;
                }

                @media (--medium) {
                    --numBrickColumns: 4;
                }

                @media (--large) {
                    --numBrickColumns: 5;
                }
            }

            &:not(.grid--custom-columns) {
                margin-inline-start: var(--bodyGutter);
                margin-inline-end: var(--bodyGutter);
                padding-inline-start: 0;
                padding-inline-end: 0;

                @include room-grid;

                .grid-item {
                    padding: 0 0 15px;
                }
            }
        }

        &.grid--link-lockups {
            --numSearchColumns: 1;
            grid-template-columns: repeat(var(--numSearchColumns), 1fr);
            row-gap: 0;

            .grid-item {
                border-top: var(--keyline-border-style);
            }

            .grid-item:first-of-type {
                border-top: none;
            }

            @media (--small) {
                --numSearchColumns: 2;
                padding-top: 18px;

                .grid-item:nth-of-type(-n + 2) {
                    border-top: none;
                }
            }

            @media (--medium) {
                --numSearchColumns: 3;

                .grid-item:nth-of-type(-n + 3) {
                    border-top: none;
                }
            }
        }

        &.grid--rectangular-link-lockups {
            --numSearchColumns: 1;
            grid-template-columns: repeat(var(--numSearchColumns), 1fr);
            row-gap: 0;

            .grid-item:first-of-type {
                border-top: none;
            }

            @media (--small) {
                --numSearchColumns: 2;
                padding-top: 18px;
            }

            @media (--medium) {
                --numSearchColumns: 3;
            }
        }

        &.grid--flow-column {
            @media (--small) {
                columns: 3;
                column-gap: $grid-gap-large;
            }
        }
    }

    .grid-section-title-container {
        :global(.header) {
            align-items: center;
        }

        :global(.title) {
            margin-top: 3px;
            font: var(--title-2-emphasized-serif);
        }

        :global(.headline + .title) {
            margin-top: 0;
        }

        :global(.headline) {
            margin-top: 5px;
        }

        :global(.subtitle) {
            margin-top: 2px;
        }

        :global(.chevron) {
            height: 15px;
        }
    }

    @include target-safari {
        .grid-item {
            min-width: 0; // Prevent grid/flex blowout
        }
    }
</style>
